/**
 * AppSync GraphQL for the admin notification centre (Trello #192).
 *
 * The subscription selection set MUST match the fields the backend mutation publishes
 * (src/lib/notifications/appsync-admin-alerts.ts) — AppSync delivers only the
 * intersection of the two selection sets, so any field omitted here is silently
 * dropped from what the dashboard receives. Keep in lockstep with the schema in
 * legal-ai-backend/terraform/appsync.tf.
 */

import type { AdminAlertType, AdminAlertSeverity, AdminStackKey } from '../services/api';

export const ON_ADMIN_ALERT_SUBSCRIPTION = /* GraphQL */ `
  subscription OnAdminAlert($stack: String!) {
    onAdminAlert(stack: $stack) {
      id
      stack
      type
      severity
      title
      message
      entityId
      entityType
      metadata
      createdAt
    }
  }
`;

/** Shape AppSync delivers for onAdminAlert (metadata arrives as an AWSJSON string). */
export interface OnAdminAlertPayload {
  id: string;
  stack: string;
  type: string;
  severity?: string | null;
  title?: string | null;
  message?: string | null;
  entityId?: string | null;
  entityType?: string | null;
  metadata?: string | null;
  createdAt: string;
}

/**
 * Which admin stack this build subscribes to. Broker-admin builds set
 * VITE_IS_BROKER_ADMIN=true (the same flag the rest of the app already uses);
 * everything else is the intelligent-automation stack. Mirrors the backend's
 * adminStack derivation (broker Cognito pool -> broker-ai).
 */
export function getAdminStack(): AdminStackKey {
  return import.meta.env.VITE_IS_BROKER_ADMIN === 'true' ? 'broker-ai' : 'intelligent-automation';
}

/** Re-exported for consumers that only import from this module. */
export type { AdminAlertType, AdminAlertSeverity, AdminStackKey };
